import MapOutlined from "@mui/icons-material/MapOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { getApartmentsMap } from "src/apis/controllers/dashboard";
import { normalizeApartmentsMapData } from "src/pages/Dashboard/dashboardData";
import GoogleApartmentMap from "./GoogleApartmentMap";

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;

  return (
    error?.message ||
    error?.response?.message ||
    "Δεν ήταν δυνατή η φόρτωση του χάρτη διαμερισμάτων."
  );
};

const ApartmentsMapView = () => {
  const [mapData, setMapData] = useState(null);
  const [clustersEnabled, setClustersEnabled] = useState(true);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [pendingClustersEnabled, setPendingClustersEnabled] = useState(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError("");

    getApartmentsMap()
      .then((response) => {
        if (!cancelled) {
          setMapData(normalizeApartmentsMapData(response?.data));
        }
      })
      .catch((requestError) => {
        if (cancelled) return;
        setMapData(null);
        setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestVersion]);

  useEffect(() => {
    if (activeMarkerId !== null || pendingClustersEnabled === null) {
      return;
    }

    setClustersEnabled(pendingClustersEnabled);
    setPendingClustersEnabled(null);
  }, [activeMarkerId, pendingClustersEnabled]);

  return (
    <Paper
      component="section"
      aria-labelledby="apartments-map-title"
      variant="outlined"
      sx={{ overflow: "hidden", borderRadius: 2 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "#dff2ff",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <MapOutlined color="primary" />
          <Box>
            <Typography id="apartments-map-title" component="h2" variant="h4">
              Ενεργά διαμερίσματα
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Γεωγραφική κατανομή λειτουργικών διαμερισμάτων
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={clustersEnabled}
                onChange={(event) => {
                  const nextClustersEnabled = event.target.checked;

                  if (activeMarkerId === null) {
                    setClustersEnabled(nextClustersEnabled);
                    return;
                  }

                  setPendingClustersEnabled(nextClustersEnabled);
                  setActiveMarkerId(null);
                }}
                inputProps={{ "aria-label": "Ομαδοποίηση σημείων" }}
              />
            }
            label="Ομαδοποίηση"
          />
          <Chip
            label={`${(
              mapData?.apartmentsWithCoordinates ?? 0
            ).toLocaleString(
              "el-GR"
            )}/${(mapData?.totalApartments ?? 0).toLocaleString("el-GR")} Διαμερίσματα `}
            aria-label="Διαμερίσματα με συντεταγμένες προς σύνολο ενεργών διαμερισμάτων"
            color="primary"
            variant="outlined"
          />
        </Stack>
      </Stack>

      {loading ? (
        <Box
          role="status"
          sx={{ minHeight: 460, display: "grid", placeItems: "center" }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress />
            <Typography color="text.secondary">
              Φόρτωση διαμερισμάτων…
            </Typography>
          </Stack>
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setRequestVersion((version) => version + 1)}
            >
              Νέα προσπάθεια
            </Button>
          }
          sx={{ m: 2 }}
        >
          {error}
        </Alert>
      ) : (
        <Box>
          <GoogleApartmentMap
            apartments={mapData.apartments}
            clustersEnabled={clustersEnabled}
            activeMarkerId={activeMarkerId}
            onActiveMarkerChange={setActiveMarkerId}
            onOpenApplication={(applicationId, apartmentId) =>
              window.open(
                `/govnr/mhds/${applicationId}/apartments/${apartmentId}`,
                "_blank",
              )
            }
          />
        </Box>
      )}
    </Paper>
  );
};

export default ApartmentsMapView;
